import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import {
	ISupportGitHubCreateIssueRequest,
	ISupportGitHubEscalateRequest,
	ISupportGitHubPanel,
	ISupportGitHubTriageRun,
} from '../../models/Support/ISupportGitHub';
import { isNotFoundError, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { invalidateTicket, SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

/**
 * Every GitHub mutation talks to GitHub before persisting, so a failure can still leave the stored
 * panel, ticket and threads changed. Both outcomes therefore refetch rather than trusting the cache.
 * Callers surface the error message itself — there is no MutationCache to do it for them.
 */
function invalidateSupportGitHubTicketState(queryClient: QueryClient, ticketId: string) {
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.github(ticketId) });
	invalidateTicket(queryClient, ticketId);
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.threads(ticketId) });
}

export function useSupportGitHubQuery(ticketId: string) {
	const userSession = useUserSession();

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.tickets.github(ticketId),
		staleTime: 5_000,
		refetchOnMount: 'always',
		// A 404 (integration absent in this environment) must not be retried three times.
		retry: supportQueryRetry,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(SUPPORT_PATHS.github.panel(ticketId), 'GET'),
		enabled: !!ticketId,
		meta: {
			// A 404 means the GitHub integration endpoints don't exist in this environment;
			// GitHubPanel renders nothing at all in that case, so suppress the toast.
			toastErrorMessage: (error: Error) => (isNotFoundError(error) ? '' : 'Failed to load linked GitHub issues'),
		},
	});
}

/**
 * Investigation history for one linked issue. Fetched only once the agent opens the history
 * disclosure, so a panel load never pays for it and a history failure never hides the panel.
 */
export function useSupportGitHubIssueRunsQuery(ticketId: string, linkId: string, enabled: boolean) {
	const userSession = useUserSession();

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.tickets.githubIssueRuns(ticketId, linkId),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISupportGitHubTriageRun[]>(
				SUPPORT_PATHS.github.issueRuns(ticketId, linkId),
				'GET'
			),
		retry: supportQueryRetry,
		// Read on open and not again until the next open: the panel deliberately never polls.
		staleTime: 0,
		refetchOnWindowFocus: false,
		enabled: enabled && !!ticketId && !!linkId,
		meta: {
			toastErrorMessage: 'Failed to load the investigation history',
		},
	});
}

export function useLinkGitHubIssueLinkMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (url: string) =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(SUPPORT_PATHS.github.linkIssue(ticketId), 'POST', {
				url,
			}),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
		onError: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}

export function useCreateGitHubIssueMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: ISupportGitHubCreateIssueRequest) =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(
				SUPPORT_PATHS.github.issues(ticketId),
				'POST',
				payload
			),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
		onError: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}

export function useUnlinkGitHubIssueLinkMutation(ticketId: string, linkId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(
				SUPPORT_PATHS.github.issue(ticketId, linkId),
				'DELETE'
			),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
		onError: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}

export function useSyncGitHubIssueMutation(ticketId: string, linkId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(
				SUPPORT_PATHS.github.syncIssue(ticketId, linkId),
				'POST'
			),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
		onError: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}

export function useEscalateGitHubIssueMutation(ticketId: string, linkId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: ISupportGitHubEscalateRequest) =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(
				SUPPORT_PATHS.github.escalateIssue(ticketId, linkId),
				'POST',
				payload
			),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
		onError: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}
